import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setApplicationType } from "../../Reducer/ApplicationTypeReducer";
import {
  getLandingPath,
  isPathAllowed,
} from "../../Components/Sidebar/sidebar.config";
import { RootState } from "../../store";
import DocumentTypeCard from "./Components/DocumentTypeCard/DocumentTypeCard";
import SelectedClientCard from "../../Components/SelectedClientCard/SelectedClientCard";
import {
  OverviewCard,
  getOverviewDocumentTypes,
} from "../../Services/OverviewService/OverviewService";
import Loader from "../../Components/Loader/Loader";
import "./Overview.css";

const Overview = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedType = useSelector(
    (state: RootState) => state.applicationType.applicationType,
  );
  const selectedClient = useSelector(
    (state: RootState) => state.selectedClient.selectedClient,
  );
  const [overviewCards, setOverviewCards] = useState<OverviewCard[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isCancelled = false;

    const loadOverviewData = async () => {
      setIsLoading(true);

      const documentTypes = await getOverviewDocumentTypes(
        selectedClient?.clientId ?? null,
      );

      if (isCancelled) return;

      setOverviewCards(documentTypes);
      setIsLoading(false);
    };

    loadOverviewData();

    return () => {
      isCancelled = true;
    };
  }, [selectedClient?.clientId]);

  const handleSelect = (card: OverviewCard) => {
    if (card.disabled || !card.value) return;

    dispatch(
      setApplicationType({
        applicationType: card.value,
        encryptedKey: card.encryptedKey,
      }),
    );

    // Fall back to the first page the user can actually open, in case the
    // card's path isn't available for their role or application type.
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const access = {
      roleId: user?.roleId,
      hasSelectedClient: Boolean(selectedClient) || user?.roleId === 1,
      applicationType: card.value,
    };

    navigate(
      isPathAllowed(card.path, access) ? card.path : getLandingPath(access),
    );
  };

  return (
    <div className="overview">
      <SelectedClientCard
        density="full"
        className="overview__client-card"
      />
      <h1 className="overview__title">Choose Document Type</h1>
      <p className="overview__subtitle">
        Select the type of documents you want to manage for this client.
      </p>

      {isLoading ? (
        <Loader />
      ) : (
        <div className="overview__cards">
          {overviewCards.map((card) => (
            <DocumentTypeCard
              key={card.section}
              title={card.title}
              icon={<card.Icon />}
              description={card.description}
              stats={card.stats}
              count={card.totalItems}
              selected={selectedType === card.value}
              disabled={card.disabled}
              className={`doc-card--${card.value ?? "default"}`}
              onClick={() => handleSelect(card)}
            />
          ))}
        </div>
      )}

      {!isLoading && overviewCards.length > 0 && (
        <p className="overview__guidance">
          You can switch between document types at any time. Each document type
          maintains its own independent workflow and data for the selected
          client.
        </p>
      )}
    </div>
  );
};

export default Overview;
